import React, { useState, useEffect, useRef, useContext } from "react";
import { MarketContext } from "../context/MarketContext";

const ChartModal = ({ symbol, onClose }) => {
  const { ticks, getLtp, addSubscribedSymbols } = useContext(MarketContext);
  const canvasRef = useRef(null);
  const [candles, setCandles] = useState([]);
  const [chartType, setChartType] = useState("candle"); // candle, line
  const [hoveredCandle, setHoveredCandle] = useState(null);
  const [mousePos, setMousePos] = useState({ x: -1, y: -1 });

  const liveTick = ticks[symbol] || {};
  const currentLtp = getLtp(symbol, 1500.0);

  // Subscribe symbol to live WS feed when modal opens
  useEffect(() => {
    if (symbol) {
      addSubscribedSymbols([symbol]);
    }
  }, [symbol, addSubscribedSymbols]);

  // Generate historical candles anchored to current LTP
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const base = currentLtp || 1500.0;
    const data = [];
    let price = base * 0.985;
    const now = new Date();

    for (let i = 40; i >= 0; i--) {
      const timeStr = new Date(now.getTime() - i * 60000).toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      });

      const change = (Math.random() - 0.47) * (base * 0.006);
      const open = price;
      const close = i === 0 ? base : open + change;
      const high = Math.max(open, close) + Math.random() * (base * 0.003);
      const low = Math.min(open, close) - Math.random() * (base * 0.003);
      const volume = Math.floor(Math.random() * 5000) + 500;

      data.push({
        time: timeStr,
        open: Number(open.toFixed(2)),
        high: Number(high.toFixed(2)),
        low: Number(low.toFixed(2)),
        close: Number(close.toFixed(2)),
        volume
      });

      price = close;
    }

    setCandles(data);
  }, [symbol]);

  // Append new live ticks dynamically to the latest candle
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!currentLtp || candles.length === 0) return;

    setCandles((prev) => {
      if (prev.length === 0) return prev;
      const last = { ...prev[prev.length - 1] };
      last.close = currentLtp;
      last.high = Math.max(last.high, currentLtp);
      last.low = Math.min(last.low, currentLtp);

      const copy = [...prev];
      copy[copy.length - 1] = last;
      return copy;
    });
  }, [currentLtp]);

  // ─── Render Canvas Chart ──────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || candles.length === 0) return;

    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    const paddingRight = 65; // Y-axis price label area
    const paddingBottom = 30; // X-axis time label area
    const chartWidth = width - paddingRight;
    const chartHeight = height - paddingBottom;

    ctx.clearRect(0, 0, width, height);

    // Calculate Y-axis price scale with 3% margin top & bottom
    const allLows = candles.map((c) => c.low);
    const allHighs = candles.map((c) => c.high);
    const minPrice = Math.min(...allLows);
    const maxPrice = Math.max(...allHighs);
    const pricePadding = (maxPrice - minPrice) * 0.08 || 1;
    const yMin = minPrice - pricePadding;
    const yMax = maxPrice + pricePadding;
    const priceRange = yMax - yMin;

    const priceToY = (price) => chartHeight - ((price - yMin) / priceRange) * chartHeight;

    // ─── Gridlines ────────────────────────────────────────────────────────
    ctx.strokeStyle = "#f0f3f8";
    ctx.lineWidth = 1;

    // Horizontal Price Gridlines
    const steps = 5;
    for (let i = 0; i <= steps; i++) {
      const p = yMin + (priceRange / steps) * i;
      const y = priceToY(p);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(chartWidth, y);
      ctx.stroke();

      // Price Label
      ctx.fillStyle = "#787b86";
      ctx.font = "11px -apple-system, BlinkMacSystemFont, Roboto, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(`₹${p.toFixed(2)}`, chartWidth + 6, y + 4);
    }

    // ─── Draw Candlesticks or Line ─────────────────────────────────────────
    const numCandles = candles.length;
    const slotWidth = chartWidth / numCandles;
    const candleWidth = Math.max(3, slotWidth * 0.65);

    if (chartType === "candle") {
      candles.forEach((c, idx) => {
        const x = idx * slotWidth + slotWidth / 2;
        const openY = priceToY(c.open);
        const closeY = priceToY(c.close);
        const highY = priceToY(c.high);
        const lowY = priceToY(c.low);
        const isBull = c.close >= c.open;
        const color = isBull ? "#089981" : "#f23645";

        // Wick line
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x, highY);
        ctx.lineTo(x, lowY);
        ctx.stroke();

        // Body rect
        ctx.fillStyle = color;
        const bodyTop = Math.min(openY, closeY);
        const bodyHeight = Math.max(2, Math.abs(closeY - openY));
        ctx.fillRect(x - candleWidth / 2, bodyTop, candleWidth, bodyHeight);
      });
    } else {
      // Line Chart with Gradient Fill
      const gradient = ctx.createLinearGradient(0, 0, 0, chartHeight);
      gradient.addColorStop(0, "rgba(41, 98, 255, 0.25)");
      gradient.addColorStop(1, "rgba(41, 98, 255, 0.0)");

      ctx.beginPath();
      candles.forEach((c, idx) => {
        const x = idx * slotWidth + slotWidth / 2;
        const y = priceToY(c.close);
        if (idx === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });

      // Area fill
      const lastX = (candles.length - 1) * slotWidth + slotWidth / 2;
      ctx.lineTo(lastX, chartHeight);
      ctx.lineTo(slotWidth / 2, chartHeight);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();

      // Line stroke
      ctx.beginPath();
      candles.forEach((c, idx) => {
        const x = idx * slotWidth + slotWidth / 2;
        const y = priceToY(c.close);
        if (idx === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = "#2962ff";
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // ─── Time Axis Labels ─────────────────────────────────────────────────
    ctx.fillStyle = "#787b86";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "center";
    const timeStep = Math.ceil(numCandles / 6);
    candles.forEach((c, idx) => {
      if (idx % timeStep === 0) {
        const x = idx * slotWidth + slotWidth / 2;
        ctx.fillText(c.time, x, height - 8);
      }
    });

    // ─── Live Price Dotted Marker ─────────────────────────────────────────
    const latestLtpY = priceToY(currentLtp);
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = "#2962ff";
    ctx.beginPath();
    ctx.moveTo(0, latestLtpY);
    ctx.lineTo(chartWidth, latestLtpY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Live Price Tag on Y-axis
    ctx.fillStyle = "#2962ff";
    ctx.fillRect(chartWidth + 2, latestLtpY - 10, paddingRight - 4, 20);
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 10px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`₹${currentLtp.toFixed(2)}`, chartWidth + paddingRight / 2, latestLtpY + 3);

    // ─── Crosshair Lines ─────────────────────────────────────────────────
    if (mousePos.x >= 0 && mousePos.x <= chartWidth && mousePos.y >= 0 && mousePos.y <= chartHeight) {
      ctx.strokeStyle = "#9598a1";
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);

      // Vertical line
      ctx.beginPath();
      ctx.moveTo(mousePos.x, 0);
      ctx.lineTo(mousePos.x, chartHeight);
      ctx.stroke();

      // Horizontal line
      ctx.beginPath();
      ctx.moveTo(0, mousePos.y);
      ctx.lineTo(chartWidth, mousePos.y);
      ctx.stroke();

      ctx.setLineDash([]);

      // Highlight hovered candle index
      const hoverIndex = Math.min(numCandles - 1, Math.max(0, Math.floor(mousePos.x / slotWidth)));
      setHoveredCandle(candles[hoverIndex]);
    }
  }, [candles, chartType, mousePos, currentLtp]);

  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);
    setMousePos({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePos({ x: -1, y: -1 });
    setHoveredCandle(null);
  };

  const displayCandle = hoveredCandle || (candles.length > 0 ? candles[candles.length - 1] : null);
  const isUp = liveTick.isDown === false;

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(10, 14, 23, 0.75)", backdropFilter: "blur(4px)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ backgroundColor: "#ffffff", width: "820px", borderRadius: "12px", padding: "24px", boxShadow: "0 20px 50px rgba(0,0,0,0.3)", display: "flex", flexDirection: "column", gap: "16px" }}>
        
        {/* Header Bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #edf2f7", paddingBottom: "14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <span style={{ fontSize: "20px", fontWeight: "bold", color: "#131722" }}>{symbol}</span>
            <span style={{ fontSize: "18px", fontWeight: "bold", color: isUp ? "#089981" : "#f23645" }}>
              ₹{currentLtp ? currentLtp.toFixed(2) : "0.00"}
            </span>
            <span style={{ fontSize: "12px", fontWeight: "bold", color: isUp ? "#089981" : "#f23645", padding: "2px 8px", borderRadius: "4px", backgroundColor: isUp ? "rgba(8, 153, 129, 0.1)" : "rgba(242, 54, 69, 0.1)" }}>
              {liveTick.percentChange || "+0.00%"}
            </span>
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {/* Chart Type Selector */}
            <div style={{ backgroundColor: "#f0f3f8", borderRadius: "6px", padding: "2px", display: "flex", gap: "2px" }}>
              <button onClick={() => setChartType("candle")} style={{ padding: "4px 10px", border: "none", borderRadius: "4px", fontSize: "12px", fontWeight: "bold", cursor: "pointer", backgroundColor: chartType === "candle" ? "#fff" : "transparent", color: chartType === "candle" ? "#2962ff" : "#616773", boxShadow: chartType === "candle" ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
                📊 Candle
              </button>
              <button onClick={() => setChartType("line")} style={{ padding: "4px 10px", border: "none", borderRadius: "4px", fontSize: "12px", fontWeight: "bold", cursor: "pointer", backgroundColor: chartType === "line" ? "#fff" : "transparent", color: chartType === "line" ? "#2962ff" : "#616773", boxShadow: chartType === "line" ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
                📈 Line
              </button>
            </div>

            <button onClick={onClose} style={{ border: "none", backgroundColor: "#f0f3f8", padding: "6px 14px", borderRadius: "6px", cursor: "pointer", fontWeight: "bold", color: "#616773" }}>
              ✕ Close
            </button>
          </div>
        </div>

        {/* OHLC Bar */}
        {displayCandle && (
          <div style={{ display: "flex", gap: "20px", fontSize: "12px", color: "#616773", backgroundColor: "#f8fafc", padding: "8px 14px", borderRadius: "6px" }}>
            <span>Time: <strong>{displayCandle.time}</strong></span>
            <span>O: <strong style={{ color: "#131722" }}>₹{displayCandle.open}</strong></span>
            <span>H: <strong style={{ color: "#089981" }}>₹{displayCandle.high}</strong></span>
            <span>L: <strong style={{ color: "#f23645" }}>₹{displayCandle.low}</strong></span>
            <span>C: <strong style={{ color: "#131722" }}>₹{displayCandle.close}</strong></span>
            <span>Vol: <strong style={{ color: "#2962ff" }}>{displayCandle.volume?.toLocaleString()}</strong></span>
          </div>
        )}

        {/* HTML5 Canvas Chart */}
        <div style={{ position: "relative", width: "100%", height: "380px" }}>
          <canvas
            ref={canvasRef}
            width={770}
            height={380}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ width: "100%", height: "100%", display: "block", cursor: "crosshair" }}
          />
        </div>
      </div>
    </div>
  );
};

export default ChartModal;
